// Classifies a flat tag string into one of Ecliptic's tag families.
// Families are ordered in canonical "how-you-say-the-trick" order so the picker
// shows stickers in the same sequence a rider would name the trick.

export type StickerFamily =
    | "Direction"
    | "Sauce"
    | "Axis"
    | "Degree"
    | "Grab"
    | "Style"
    | "Feature"
    | "Activity"
    | "Generic";

export const CANONICAL_FAMILY_ORDER: StickerFamily[] = [
    "Direction",
    "Sauce",
    "Axis",
    "Degree",
    "Grab",
    "Style",
    "Feature",
    "Activity",
    "Generic",
];

const DIRECTION_TOKENS = [
    "switch", "frontside", "backside", "front", "back", "fs", "bs",
    "alley oop", "alleyoop", "unnatural", "left", "right",
    "switch left", "switch right",
];

const SAUCE_TOKENS = [
    "nollie", "ollie", "butter", "hand drag", "tailpress", "nosebutter",
    "nosepress", "tailbutter", "stomp", "drop", "press",
];

// Axis includes well-known multi-rotation names and "flip" family.
const AXIS_TOKENS = [
    "cork", "double cork", "triple cork", "quad cork", "quint cork",
    "flatspin", "misty", "rodeo", "bio", "loop", "underflip", "upright",
    "frontflip", "backflip", "front flip", "back flip",
    "pretzel", "screw", "lincoln loop",
];

const GRAB_TOKENS = [
    "mute", "indy", "tail", "nose", "stalefish", "melon", "japan", "blunt",
    "safety", "crail", "tindy", "truck driver", "method", "seatbelt",
    "rocket", "tweak", "double grab", "stiffy", "shifty",
];

const STYLE_TOKENS = ["forward", "switch up"];

const FEATURE_TOKENS = [
    "50-50", "5050", "boardslide", "lipslide", "wallride", "noseslide",
    "tailslide", "smith", "feeble", "crooked", "bluntslide", "noseblunt",
];

function normalise(tag: string): string {
    return tag.trim().toLowerCase();
}

function isDegree(tag: string): boolean {
    // Pure number degree (180, 360, 540, ... 2160) or with " Bring Back" suffix.
    const lower = normalise(tag);
    if (/^\d{2,4}$/.test(lower)) return true;
    if (/^\d{2,4}\s+bring\s+back$/.test(lower)) return true;
    if (lower === "zero" || lower === "air") return true;
    return false;
}

function matchesAny(tag: string, tokens: string[]): boolean {
    const lower = normalise(tag);
    return tokens.some(t => lower === t || lower.includes(t));
}

export function classifyTag(tag: string): StickerFamily {
    if (!tag) return "Generic";
    if (isDegree(tag)) return "Degree";
    if (matchesAny(tag, DIRECTION_TOKENS)) return "Direction";
    if (matchesAny(tag, SAUCE_TOKENS)) return "Sauce";
    if (matchesAny(tag, AXIS_TOKENS)) return "Axis";
    if (matchesAny(tag, GRAB_TOKENS)) return "Grab";
    if (matchesAny(tag, FEATURE_TOKENS)) return "Feature";
    if (matchesAny(tag, STYLE_TOKENS)) return "Style";
    return "Generic";
}

export interface ClassifiedTag {
    tag: string;
    family: StickerFamily;
}

export function classifyAndOrderTags(tags: string[]): ClassifiedTag[] {
    const classified = tags.map(t => ({ tag: t, family: classifyTag(t) }));
    const orderIndex = (f: StickerFamily) => CANONICAL_FAMILY_ORDER.indexOf(f);
    return classified.sort((a, b) => orderIndex(a.family) - orderIndex(b.family));
}
